import Skeleton from "components/Skeleton/Skeleton";

/**
 * Branded loading shell shown during auth checking and WHOAMI.
 * Mirrors the real AppShell layout (sidebar + header + content)
 * so there's zero layout shift when the authenticated app mounts.
 */
const LoadingShell = () => (
  <div className="h-screen bg-muted lg:p-1.5">
    <div className="h-full flex overflow-hidden lg:rounded-xl lg:border lg:border-border/40 lg:shadow-sm">

      {/* ── Sidebar skeleton (desktop only) ── */}
      <div className="hidden lg:flex lg:flex-col lg:shrink-0 lg:w-52 bg-card border-r border-border/50">
        {/* Logo area — matches DesktopSidebar h-11 px-3.5 */}
        <div className="flex items-center shrink-0 h-11 px-3.5">
          <img
            src="/image/logo.svg"
            alt="Central Valuations"
            draggable="false"
            className="h-6 brightness-0 dark:invert"
          />
        </div>

        {/* Nav item placeholders */}
        <div className="flex-1 px-2 pt-2 space-y-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-md" />
          ))}
        </div>

        {/* Bottom user area placeholder */}
        <div className="px-2 pb-3 mt-auto">
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>

      {/* ── Main content area ── */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden bg-background">

        {/* Mobile header — matches Header.js: lg:hidden, bg-card, border-b */}
        <div className="lg:hidden relative z-10 flex-shrink-0 flex items-center h-11 bg-card border-b border-border px-4">
          <img
            src="/image/logo.svg"
            alt="Central Valuations"
            draggable="false"
            className="h-5 brightness-0 dark:invert"
          />
        </div>

        {/* Content — mirrors valuation list skeleton */}
        <div className="flex-1 overflow-y-auto bg-muted">
          {/* Page heading bar */}
          <div className="bg-card border-b border-border/30">
            <div className="h-11 px-4 lg:px-5 max-w-page mx-auto flex items-center">
              <Skeleton variant="text" className="w-28 h-4" />
            </div>
          </div>

          {/* Content area */}
          <div className="mx-auto w-full max-w-page px-4 lg:px-5 py-5">
            {/* SLA cards row */}
            <div className="flex gap-3 mb-4">
              <Skeleton className="flex-1 h-16 rounded-xl" />
              <Skeleton className="flex-1 h-16 rounded-xl" />
              <Skeleton className="flex-1 h-16 rounded-xl hidden sm:block" />
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Skeleton className="w-28 h-8 rounded-lg" />
              <Skeleton className="w-28 h-8 rounded-lg" />
              <Skeleton className="w-24 h-8 rounded-lg hidden sm:block" />
            </div>

            {/* Table card */}
            <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
              {/* Table header */}
              <div className="flex items-center gap-4 py-2.5 px-4 border-b border-border/30">
                <Skeleton variant="text" className="w-20 h-3" />
                <Skeleton variant="text" className="w-16 h-3 hidden lg:block" />
                <Skeleton variant="text" className="w-24 h-3 hidden lg:block" />
                <Skeleton variant="text" className="w-16 h-3 hidden lg:block" />
                <Skeleton variant="text" className="w-16 h-3 hidden lg:block" />
              </div>

              {/* Table rows */}
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 py-3 px-4 border-b border-border/10 last:border-b-0"
                >
                  <Skeleton variant="text" className="w-1/3 h-3.5" />
                  <Skeleton variant="text" className="w-16 h-3.5 hidden lg:block" />
                  <Skeleton variant="text" className="w-20 h-3.5 hidden lg:block" />
                  <Skeleton className="w-16 h-5 rounded-full hidden lg:block" />
                  <Skeleton variant="text" className="w-16 h-3.5 hidden lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile bottom nav spacer */}
        <div className="lg:hidden h-24" />
      </div>
    </div>
  </div>
);

export default LoadingShell;
